
import { defineComponent, computed, reactive, toRefs, PropType, ref, onMounted, watch } from 'vue'
import orderBy from 'lodash.orderby'
import template from 'lodash.template'
import templatesettings from 'lodash.templatesettings'
import Logo from './Logo.vue';
import Details from './Details.vue';
import { FontType, Icon } from '../types'
import codePoints from '../data/codePoints.json'
import { host, asset_url_pattern } from '../data/metadata.json'

templatesettings.interpolate = /{([\s\S]+?)}/g
const compiled_asset_url = template('https://' + host + asset_url_pattern)

type ColorMode = 'auto' | 'light' | 'dark';
interface State {
  font: FontType;
  sort: string;
  showCodepoint: boolean;
  colorMode: ColorMode;
  selectedName: string;
}

export default defineComponent({
  name: 'Main',
  components: {
    Logo,
    Details,
  },
  props: {
    icons: {
      type: Array as PropType<Icon[]>,
      required: true
    },
    searchText: {
      type: String,
      required: true
    },
  },
  setup(props) {
    const state = reactive<State>({
      font: 'filled',
      sort: 'popularity',
      colorMode: 'auto',
      showCodepoint: false,
      selectedName: "",
    })
    const searchRef = ref<HTMLInputElement>()
    onMounted(() => {
      state.colorMode = window.matchMedia("(prefers-color-scheme: dark)").matches ? 'dark' : 'light'
    })
    watch(
      () => ({...state}),
      (next, prev) => {
        if (next.colorMode !== prev.colorMode) {
          document.documentElement.setAttribute('data-color-mode', next.colorMode)
        }
      },
    )
    const visibilityChanged = (isVisible: boolean) => {
      if (!isVisible) {
        state.selectedName = ""
      }
    }
    const changeColorMode = (color: ColorMode) => {
      state.colorMode = color
    }
    const placeholder = computed(() => `Search ${props.icons.length > 0 ? props.icons.length.toLocaleString() : ''} icons (Press "/" to focus)`)

    const select = (name: string|undefined = "") => {
      if (state.selectedName === name) {
        state.selectedName = ""
      } else {
        state.selectedName = name
      }
    }
    const toggleShowCodepoint = () => state.showCodepoint = !state.showCodepoint;
    const filter = () => props.icons.filter((icon: Icon) => {
      if (icon.font !== state.font) {
        return false
      }
      return true
    })
    const filteredIcons = computed(() => orderBy(filter(), [state.sort], [state.sort === 'popularity' ? 'desc' : 'asc']))
    const selectedIcon = computed(() => {
      const icon = props.icons.find((icon: Icon) => icon.name === state.selectedName)
      const family = codePoints.find(code => code.font === state.font)?.['family'] || ''
      let svgAssetUrl = null
      let pngAssetUrl = null
      if (icon && !icon?.unsupported_families?.includes(family)) {
        const size = icon.sizes_px?.[icon.sizes_px?.length - 1]
        const color = 'black'
        svgAssetUrl = compiled_asset_url({
          family: family.replaceAll(' ', '').toLowerCase(),
          icon: icon.name,
          version: icon.version,
          asset: `${size}px.svg`,
        })
        pngAssetUrl = compiled_asset_url({
          family: family.replaceAll(' ', '').toLowerCase(),
          icon: icon.name,
          version: icon.version,
          asset: `${color}-${size}dp.zip`,
        })
      }
      return {
        ...icon,
        svgAssetUrl,
        pngAssetUrl,
        onClose: () => select(),
      }
    })
    const onChangeFont = (value: FontType) => state.font = value
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Slash') {
        if (searchRef.value) {
          searchRef.value.focus()
        }
      } else if (event.code === 'Escape' && state.selectedName) {
        select()
      }
    }
    document.addEventListener('keyup', onKeyDown)
    return {
      ...toRefs(state),
      codePoints,
      select,
      toggleShowCodepoint,
      filteredIcons,
      selectedIcon,
      onChangeFont,
      placeholder,
      searchRef,
      changeColorMode,
      visibilityChanged,
    }
  },
});
